import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type CompanySpecsFragment } from '../../../api/fragments/CompanySpecs';
import {
    DeleteCompanyAssetDocument,
    type DeleteCompanyAssetMutation,
    type DeleteCompanyAssetMutationVariables,
} from '../../../api/mutations/deleteCompanyAsset';
import {
    UploadCompanyAssetDocument,
    type UploadCompanyAssetMutation,
    type UploadCompanyAssetMutationVariables,
} from '../../../api/mutations/uploadCompanyAsset';
import { CompanyAsset } from '../../../api/types';
import { type CompanyFormValues } from './CompanyForm';

const useHandleCompanyAssets = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        (companyId: string, values: CompanyFormValues, initialValues?: CompanySpecsFragment) => {
            const {
                favicon,
                logo,
                font,
                fontBold,
                edmEmailFooter: { phoneIcon, arrowIcon, emailIcon },
            } = values;

            const uploads = [];

            if (favicon instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Favicon, upload: favicon },
                    })
                );
            } else if (favicon === null && isObject(initialValues.favicon)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Favicon },
                    })
                );
            }

            if (logo instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Logo, upload: logo },
                    })
                );
            } else if (logo === null && isObject(initialValues.logo)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Logo },
                    })
                );
            }

            if (font instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Font, upload: font },
                    })
                );
            } else if (font === null && isObject(initialValues.font)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.Font },
                    })
                );
            }

            if (fontBold instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.FontBold, upload: fontBold },
                    })
                );
            } else if (fontBold === null && isObject(initialValues.fontBold)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.FontBold },
                    })
                );
            }

            if (emailIcon instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailIcon, upload: emailIcon },
                    })
                );
            } else if (emailIcon === null && isObject(initialValues.edmEmailFooter.emailIcon)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailIcon },
                    })
                );
            }

            if (arrowIcon instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailArrowIcon, upload: arrowIcon },
                    })
                );
            } else if (arrowIcon === null && isObject(initialValues.edmEmailFooter.arrowIcon)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailArrowIcon },
                    })
                );
            }

            if (phoneIcon instanceof File) {
                uploads.push(
                    apolloClient.mutate<UploadCompanyAssetMutation, UploadCompanyAssetMutationVariables>({
                        mutation: UploadCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailPhoneIcon, upload: phoneIcon },
                    })
                );
            } else if (phoneIcon === null && isObject(initialValues.edmEmailFooter.phoneIcon)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<DeleteCompanyAssetMutation, DeleteCompanyAssetMutationVariables>({
                        mutation: DeleteCompanyAssetDocument,
                        variables: { companyId, assetType: CompanyAsset.EdmEmailPhoneIcon },
                    })
                );
            }

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [apolloClient]
    );
};

export default useHandleCompanyAssets;
